import { CONSTANTS, CANVAS_ELEMENT_SNAPPING } from '../PreviewDisplayConstants';
import { measurePaintedFrame } from './previewFrameGeometry';
import { measureFrame } from './canvasElementSnapping';
import { buildLinedGradient } from './linedOverlayStyle';

// Draws the alignment lines a drag has caught on, one across the frame per axis. Like the bounding
// indicator, this is editor chrome and lives in the DOM above the canvas rather than in the PIXI
// stage: the stage is what getExtractedCanvas() rasterises into the saved video thumbnail, and a guide
// line baked into a user's thumbnail is not something they can undo.
export default class CanvasElementSnapGuides {

    constructor(container, canvas) {
        this.container = container;
        this.canvas = canvas;

        this.lines = {
            x: this.createLine('x'),
            y: this.createLine('y')
        };
    }

    createLine(axis) {
        const element = document.createElement('div');
        element.setAttribute('aria-hidden', 'true');
        element.setAttribute('data-canvas-element-snap-guide', axis);

        Object.assign(element.style, {
            position: 'absolute',
            display: 'none',
            // The guide appears under the pointer mid-drag and must never take it, or the drag would
            // end the moment a line was caught.
            pointerEvents: 'none',
            // Above the outline, so a guide running along an edge of the picture stays readable.
            zIndex: 3,
            // Segments run along the line, so a vertical guide is striped top to bottom.
            backgroundImage: buildLinedGradient(axis === 'x' ? '180deg' : '90deg')
        });

        this.container.appendChild(element);

        return element;
    }

    // `guides` is what snapBoxCenter handed back: an entry per snapped axis, carrying the line it
    // caught in base units. An axis with no entry is dragging freely and its guide is put away.
    show(guides) {
        const paintedFrame = measurePaintedFrame(this.canvas);
        const baseUnitFrame = measureFrame();
        const containerBounds = this.container.getBoundingClientRect();

        Object.keys(this.lines).forEach(axis => {
            const guide = guides.find(one => one.axis === axis && isWithinFrame(one, baseUnitFrame));

            if (guide)
                this.placeLine(axis, guide.line, baseUnitFrame, paintedFrame, containerBounds);
            else
                this.hideLine(axis);
        });
    }

    placeLine(axis, line, baseUnitFrame, paintedFrame, containerBounds) {
        const element = this.lines[axis];
        const thickness = CANVAS_ELEMENT_SNAPPING.GUIDE_THICKNESS;

        // A base unit line measured from the middle of the frame, back onto the painted rectangle.
        const alongFrame = axis === 'x'
            ? CONSTANTS.DEFAULT_WIDTH * 0.5 + line * baseUnitFrame.multiplier
            : CONSTANTS.DEFAULT_HEIGHT * 0.5 + line * baseUnitFrame.multiplier;

        const left = paintedFrame.left - containerBounds.left;
        const top = paintedFrame.top - containerBounds.top;
        const paintedWidth = this.canvas.width * paintedFrame.scale;
        const paintedHeight = this.canvas.height * paintedFrame.scale;

        if (axis === 'x') {
            element.style.left = `${left + alongFrame * paintedFrame.scale - thickness * 0.5}px`;
            element.style.top = `${top}px`;
            element.style.width = `${thickness}px`;
            element.style.height = `${paintedHeight}px`;
        } else {
            element.style.left = `${left}px`;
            element.style.top = `${top + alongFrame * paintedFrame.scale - thickness * 0.5}px`;
            element.style.width = `${paintedWidth}px`;
            element.style.height = `${thickness}px`;
        }

        element.style.display = 'block';
    }

    hideLine(axis) {
        const element = this.lines[axis];

        if (element.style.display !== 'none')
            element.style.display = 'none';
    }

    hide() {
        Object.keys(this.lines).forEach(axis => this.hideLine(axis));
    }

    destroy() {
        Object.keys(this.lines).forEach(axis => this.lines[axis].remove());
    }
}

// A guide is drawn right across the painted frame, so a line falling outside it would be a stripe
// over the letterbox bar marking an alignment nobody can see. The frame's own lines are always
// inside it; only an element left off the canvas by an earlier aspect ratio can offer one that is not.
function isWithinFrame(guide, { halfWidth, halfHeight }) {
    return Math.abs(guide.line) <= (guide.axis === 'x' ? halfWidth : halfHeight);
}
